import * as React from 'react';

import {
  type CalendarDate,
  Time,
  toCalendarDate,
  toCalendarDateTime,
} from '@internationalized/date';
import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { DatePicker, Row, Select, TextField } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import { AddressSection } from '../../components/@shared/AddressSection';
import { DriverSelect } from '../../components/@shared/DriverSelect';
import { FacilityGroupSelect } from '../../components/@shared/FacilityGroupSelect';
import { HaulerSelect } from '../../components/@shared/HaulerSelect';
import { RouteSelect } from '../../components/@shared/RouteSelect';
import { TrailerSelect } from '../../components/@shared/TrailerSelect';
import { TruckSelect } from '../../components/@shared/TruckSelect';
import {
  $serviceForm,
  $updateServiceForm,
} from '../../state/serviceForm.localState';

import { ServiceAssetSection } from './ServiceAssetSection';

import { useFacilitiesQuery } from '~/modules/transportation/hooks/queries/useFacilitiesQuery';
import { useServiceTypesListQuery } from '~/modules/transportation/hooks/queries/useServiceTypesListQuery';

type ServiceTypeSelectProps = {
  value?: string;
  onChange: (value: string | undefined) => void;
  label: string;
  isDisabled?: boolean;
  isRequired?: boolean;
};

const ServiceTypeSelect = ({
  value,
  onChange,
  label,
  isDisabled,
  isRequired,
}: ServiceTypeSelectProps) => {
  const { data } = useServiceTypesListQuery({
    filter: { isActive: true },
    pagination: { limit: 100, offset: 0 },
  });

  const options = React.useMemo(
    () =>
      (data?.serviceTypes ?? []).map((st) => ({
        key: st.id,
        label: st.name,
      })),
    [data?.serviceTypes],
  );

  return (
    <Select
      label={label}
      value={value}
      onChange={(v) => onChange(v || undefined)}
      options={options}
      isDisabled={isDisabled}
      isRequired={isRequired}
      className="@ServiceTypeSelect"
    />
  );
};

type SectionHeaderProps = { title: string };

const SectionHeader = ({ title }: SectionHeaderProps) => (
  <div className="@SectionHeader border-b border-foreground/10 pb-1 text-sm font-semibold text-foreground/70">
    {title}
  </div>
);

type ServiceFormProps = {
  isViewMode?: boolean;
  mode?: 'simple' | 'advanced';
};

export const ServiceForm = ({
  isViewMode,
  mode = 'simple',
}: ServiceFormProps) => {
  const t = useTranslations();
  const form = useAtomValue($serviceForm);
  const updateForm = useSetAtom($updateServiceForm);

  const isAdvanced = mode === 'advanced';

  const handleFieldChange =
    <K extends keyof typeof form>(field: K) =>
    (value: (typeof form)[K]) => {
      updateForm({
        [field]: value,
      });
    };

  const { data: serviceTypesData } = useServiceTypesListQuery({
    filter: { isActive: true },
    pagination: { limit: 100, offset: 0 },
  });

  const selectedServiceTypeName = React.useMemo(() => {
    if (!form.typeId || !serviceTypesData?.serviceTypes) return undefined;
    return serviceTypesData.serviceTypes.find((st) => st.id === form.typeId)
      ?.name;
  }, [form.typeId, serviceTypesData?.serviceTypes]);

  const isInteryardTransfer = selectedServiceTypeName === 'Interyard Transfer';

  const { data: facilitiesData } = useFacilitiesQuery({
    facilityGroupId: form.facilityGroupId ?? undefined,
    limit: 100,
    offset: 0,
  });

  const facilityOptions = React.useMemo(
    () =>
      (facilitiesData?.facilities ?? []).map((f) => ({
        key: f.id,
        label: f.facilityName,
      })),
    [facilitiesData?.facilities],
  );

  const handleHaulerChange = () => {
    if (form.driverId) {
      updateForm({ driverId: undefined });
    }
  };

  const isReadOnly = isViewMode || false;

  return (
    <div className="@ServiceForm flex flex-col gap-4">
      <SectionHeader title={t.servicesView.form.sections.taskDetails} />

      {isAdvanced ? (
        <Row gap3>
          <div className="flex-1">
            <ServiceTypeSelect
              value={form.typeId}
              onChange={(value) => handleFieldChange('typeId')(value)}
              label={t.servicesView.form.fields.type}
              isDisabled={isReadOnly}
              isRequired
            />
          </div>
          <div className="flex-1">
            <RouteSelect
              label={t.servicesView.form.fields.route}
              value={form.routeId ?? ''}
              onChange={(routeId, driverId) => {
                handleFieldChange('routeId')(
                  routeId?.toString() === '' ? undefined : routeId?.toString(),
                );
                handleFieldChange('driverId')(driverId || undefined);
              }}
              isDisabled={isReadOnly}
            />
          </div>
        </Row>
      ) : (
        <ServiceTypeSelect
          value={form.typeId}
          onChange={(value) => handleFieldChange('typeId')(value)}
          label={t.servicesView.form.fields.type}
          isDisabled={isReadOnly}
          isRequired
        />
      )}

      <DatePicker
        label={t.servicesView.form.fields.scheduledDate}
        value={toCalendarDate(form.scheduledWindowStart)}
        onChange={(date: CalendarDate | null) => {
          if (!date) return;
          const startOfDay = toCalendarDateTime(date, new Time(0, 0, 0));
          const endOfDay = toCalendarDateTime(date, new Time(23, 59, 59));
          handleFieldChange('scheduledWindowStart')(startOfDay);
          handleFieldChange('scheduledWindowEnd')(endOfDay);
        }}
        isRequired
        isDisabled={isReadOnly}
      />

      <SectionHeader title={t.servicesView.form.sections.location} />

      {isAdvanced ? (
        <Row gap3>
          <div className="flex-1">
            <FacilityGroupSelect
              label={t.servicesView.form.fields.facilityGroup}
              value={form.facilityGroupId ?? ''}
              onChange={(value) =>
                handleFieldChange('facilityGroupId')(
                  value?.toString() === '' ? undefined : value?.toString(),
                )
              }
              isDisabled={isReadOnly}
            />
          </div>
          <div className="flex-1">
            <Select
              label={t.servicesView.form.fields.facility}
              value={form.facilityId ?? ''}
              onChange={(value) =>
                handleFieldChange('facilityId')(
                  value?.toString() === '' ? undefined : value?.toString(),
                )
              }
              options={facilityOptions}
              isDisabled={isReadOnly}
            />
          </div>
        </Row>
      ) : (
        <FacilityGroupSelect
          label={t.servicesView.form.fields.facilityGroup}
          value={form.facilityGroupId ?? ''}
          onChange={(value) =>
            handleFieldChange('facilityGroupId')(
              value?.toString() === '' ? undefined : value?.toString(),
            )
          }
          isDisabled={isReadOnly}
        />
      )}

      {isInteryardTransfer && (
        <Select
          label={t.servicesView.form.fields.destinationFacility}
          value={form.destinationFacilityId ?? ''}
          onChange={(value) =>
            handleFieldChange('destinationFacilityId')(
              value?.toString() === '' ? undefined : value?.toString(),
            )
          }
          options={facilityOptions}
          isDisabled={isReadOnly}
        />
      )}

      <AddressSection
        onFieldChange={handleFieldChange}
        onUpdate={handleFieldChange('address')}
        isReadOnly={isReadOnly}
      />

      <SectionHeader title={t.servicesView.form.sections.actionsAndBins} />

      <ServiceAssetSection
        isReadOnly={isReadOnly}
        serviceTypeId={form.typeId}
      />

      {isAdvanced && (
        <>
          <SectionHeader title={t.servicesView.form.sections.transportation} />

          {form.relationshipType !== 'HAULER' && (
            <HaulerSelect
              label={t.servicesView.form.fields.hauler}
              value={form.haulerId || ''}
              onChange={(value) =>
                handleFieldChange('haulerId')(
                  value?.toString() === '' ? undefined : value?.toString(),
                )
              }
              onHaulerChange={handleHaulerChange}
              isDisabled={isReadOnly}
            />
          )}

          <DriverSelect
            label={t.servicesView.form.fields.driver}
            value={form.driverId ?? ''}
            onChange={(value) =>
              handleFieldChange('driverId')(
                value?.toString() === '' ? undefined : value?.toString(),
              )
            }
            haulerId={form.haulerId}
            isDisabled={isReadOnly || !!form.routeId}
          />

          <Row gap3>
            <div className="flex-1">
              <TruckSelect
                label={t.servicesView.form.fields.truck}
                value={form.truckId ?? ''}
                onChange={(value) =>
                  handleFieldChange('truckId')(
                    value?.toString() === '' ? undefined : value?.toString(),
                  )
                }
                isDisabled={isReadOnly}
              />
            </div>
            <div className="flex-1">
              <TrailerSelect
                label={t.servicesView.form.fields.trailer}
                value={form.trailerId ?? ''}
                onChange={(value) =>
                  handleFieldChange('trailerId')(
                    value?.toString() === '' ? undefined : value?.toString(),
                  )
                }
                isDisabled={isReadOnly}
              />
            </div>
          </Row>

          <SectionHeader title={t.servicesView.form.sections.otherDetails} />

          <TextField
            label={t.servicesView.form.fields.internalNotes}
            value={form.internalNotes ?? ''}
            onChange={handleFieldChange('internalNotes')}
            isDisabled={isReadOnly}
            placeholder={t.servicesView.form.placeholders.notes}
          />

          <TextField
            label={t.servicesView.form.fields.externalNotes}
            value={form.externalNotes ?? ''}
            onChange={handleFieldChange('externalNotes')}
            isDisabled={isReadOnly}
            placeholder={t.servicesView.form.placeholders.notes}
          />
        </>
      )}
    </div>
  );
};
